import { deviceAdminApi } from '@/api';
import { HelpFactoryComponent, ModalConfirmCustomFooter, NotificationStatus } from '@/components/common';
import { handleResponse } from '@/Helper';
import { SecuredComponents } from '@/model/AccountModel';
import { PaginationSetting } from '@/model/CommonModel';
import {
	CurrentDeviceControlObj,
	DeviceObjectType,
	GeneralSubPermission,
	Role,
	RoleGeneralContextModel,
	UserRole,
	VelocityConfigurationModel,
} from '@/model/DeviceAdminModel';
import { HelpFeature } from '@/model/HelpModel';
import { Logger } from '@/model/LoggingModel';
import { DigiTracPermissionItem, HardwarePermission } from '@/model/RolesModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setCurrentDevice } from '@/store/common/actions';
import { setRolesBy, setRolesByData } from '@/store/deviceControl/actions';
import { selectTablePaginationSetting, selectVelocityConfiguration } from '@/store/deviceControl/selectors';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Input, Spin, Tabs, TabsProps, notification } from 'antd';
import cx from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { batch } from 'react-redux';
import { ApplicationPermissions } from '../ApplicationPermissions/ApplicationPermissions';
import { GeneralPermissions } from '../GeneralPermissions/GeneralPermissions';
import { HardwarePermissions } from '../HardwarePermissions/HardwarePermissions';
import { Members } from '../Members/Members';
import { setClearIsDirtyAction, setHardwarePermissionsAction, setRolePropsContextModelAction, setRolesContextModelAction } from '../RoleContext/action';
import { RoleStore } from '../RoleContext/context';
import styles from './modalComponent.module.scss';

type Props = {
	currentDevice: CurrentDeviceControlObj;
};

//Avoid creating object style inline, since increases reconciliations
const roleNameDuplicateMessage: string = _('DuplicateGroupName');
const topicId: string = 'ToAddNewRole';

const ModalComponent: React.FC<Props> = ({ currentDevice }) => {
	//#region state management variables
	const dispatch = useStoreDispatch();
	const {
		roleContextInitialState: {
			props: { isDirty: isDirtyProps, roleName, roleDescription },
			member: { isDirty: isDirtyMember, subscribedOperatorIds },
			applicationPermissions: { isDirty: isDirtyApplicationPermissions, applicationPermissions },
			generalPermissions: { isDirty: isDirtyGeneralPermissions, modifiedSubPermissions },
			hardwarePermissions,
		},
		dispatcher: dispatchLocal,
	} = useContext(RoleStore);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [originalName, setOriginalName] = useState<string>('');
	const [isShowUnsavedWarningModal, setIsShowUnsavedWarningModal] = useState<boolean>(false);
	const [isRoleNameDuplicate, setIsRoleNameDuplicate] = useState<boolean>(false);
	const [isRoleNameEmpty, setIsRoleNameEmpty] = useState<boolean>(false);
	const [emptyNameErrorMessage, setEmptyNameErrorMessage] = useState<string>('');
	const [redirectTo, setRedirectTo] = useState<string>(null);

	const hasChangedRoleRef = React.useRef(false);

	const { roles: Roles }: VelocityConfigurationModel = useStoreSelector(selectVelocityConfiguration);
	const tablePaginationSetting: PaginationSetting = useStoreSelector<PaginationSetting>(selectTablePaginationSetting);
	//#endregion

	const { isDirty: isDirtyHardwarePermissions, globalTimeZoneId, modifiedHardwarePermissions } = hardwarePermissions;

	useEffect(() => {
		Promise.all([deviceAdminApi.getErrorMessages(SecuredComponents.Roles), deviceAdminApi.getRoles()])
			.then(responses => {
				const [emptyNameErrorMessageResponse, userRolesResponse] = responses;
				setEmptyNameErrorMessage(emptyNameErrorMessageResponse.EmptyName);
				if (!handleResponse(userRolesResponse)) {
					const { Entity: data } = userRolesResponse;
					if (data) {
						const userRole = data.find(d => d.Id === currentDevice.Id);
						const initState: RoleGeneralContextModel = {
							roleId: userRole.Id,
							roleName: userRole.Name,
							roleDescription: userRole.Description,
						};
						setOriginalName(initState.roleName);
						dispatchLocal(setRolesContextModelAction(data));
						flushSync(() => dispatchLocal(setRolePropsContextModelAction(initState)));
						dispatchLocal(
							setHardwarePermissionsAction({
								...hardwarePermissions,
								globalTimeZoneId: userRole.GlobalTimeZoneId,
							})
						);
					}
				}
			})
			.catch(err => {
				Logger.writeErrorLog(`${err.name}: ${err.message}`);
			})
			.finally(() => setIsLoading(false));

		window.addEventListener('click', handleClickRedirectTo, true);
		return () => {
			window.removeEventListener('click', handleClickRedirectTo, true);
		};
	}, []);

	const items: TabsProps['items'] = [
		{
			key: '1',
			label: _('Members'),
			forceRender: true,
			children: <Members />,
		},
		{
			key: '2',
			label: _('ApplicationPermissions'),
			children: <ApplicationPermissions />,
		},
		{
			key: '3',
			label: _('GeneralPermissions'),
			children: <GeneralPermissions />,
		},
		{
			key: '4',
			label: _('Hardware'),
			children: <HardwarePermissions />,
		},
	];

	const completeBackNavigationToRolesList = (updateData: boolean = false) => {
		const deviceId = currentDevice.Id;
		deviceAdminApi.unlockComponentDeviceAdmin(deviceId, SecuredComponents.Roles).finally(() => {
			window.history.replaceState({}, '', `${location.pathname}`);
			const newDevice: CurrentDeviceControlObj = {
				Id: deviceId,
				DeviceObjectType: DeviceObjectType.Roles,
				Address: 'passback',
				IsModalOpen: false,
				IsNew: false,
			};
			const updateRoles: UserRole[] = Roles.map(x => {
				let retValue = { ...x };
				if (x.Id.toString() === deviceId.toString()) {
					retValue.checked = true;
					if (updateData) {
						retValue.Name = roleName;
						retValue.Description = roleDescription;
					}
				} else {
					retValue.checked = false;
				}

				return retValue;
			});
			batch(() => {
				dispatch(setRolesBy({ ...tablePaginationSetting }));
				dispatch(setRolesByData(updateRoles));
				dispatch(setCurrentDevice(newDevice));
			});
		});
	};

	const isDirty = isDirtyProps || isDirtyMember || isDirtyApplicationPermissions || isDirtyGeneralPermissions || isDirtyHardwarePermissions;
	hasChangedRoleRef.current = isDirty;

	const handleClickRedirectTo = event => {
		const helpUrl = `${window.location.href}#`;
		const target: HTMLAnchorElement = event.target;
		const url: string = target.href !== 'javascript:void(0)' && target.href !== helpUrl ? target.href : '';
		const tagName = target?.tagName?.toLocaleLowerCase();

		if (tagName === 'a' && hasChangedRoleRef.current && url !== '') {
			event.preventDefault();
			event.stopImmediatePropagation();
			setRedirectTo(url);
			setIsShowUnsavedWarningModal(true);
		}
	};

	const handleBackRolesList = () => {
		if (isDirty) {
			setIsShowUnsavedWarningModal(true);
			return;
		}
		completeBackNavigationToRolesList();
	};

	const handleErrorNotification = (errorMessage: string = null) => {
		if (errorMessage) {
			notification['error']({
				message: errorMessage,
			});
		} else {
			notification['error']({
				message: _('OperatorEntryError'),
			});
		}
	};

	const onHandleWarningModalNo = () => {
		if (redirectTo) {
			window.location.href = redirectTo;
		} else {
			setIsShowUnsavedWarningModal(false);
			completeBackNavigationToRolesList();
		}
	};

	const onHandleWarningModalCancel = () => {
		setIsShowUnsavedWarningModal(false);
	};

	const onUnsavedChangesWarning = (): React.ReactNode => {
		if (!isShowUnsavedWarningModal) return;

		return (
			<ModalConfirmCustomFooter
				key='roleModalCustomFooter'
				title={_('Warning')}
				width={'700px'}
				visible
				onOk={onHandleSaveChanges}
				onNo={onHandleWarningModalNo}
				onCancel={onHandleWarningModalCancel}
				navigateTo={redirectTo}
				children={
					<label key='roleModalSaveChangesWarning' className={styles.saveChangesWarning}>
						{_('RolePermissionsChangesWarning')}
					</label>
				}
			/>
		);
	};

	const onHandleSaveChanges = () => {
		const name: string = roleName?.trim();
		if (!name) {
			batch(() => {
				setIsRoleNameDuplicate(false);
				setIsRoleNameEmpty(true);
				handleErrorNotification();
			});
			return;
		}

		batch(() => {
			setIsRoleNameEmpty(false);
			setIsLoading(true);
		});

		const role: Role = {
			RoleId: currentDevice.Id,
			RoleName: roleName,
			Description: roleDescription,
			RoleMember: {
				OperatorIds: subscribedOperatorIds,
			},
			ApplicationSubPermissions: applicationPermissions.flatMap(x => x.ApplicationSubPermissions),
			GeneralSubPermissions: [],
			HardwarePermissions: [],
			GlobalTimeZoneId: globalTimeZoneId,
		};

		Object.values(modifiedSubPermissions)?.forEach((modifiedSubPermissionsArray: Partial<GeneralSubPermission>[]) => {
			role.GeneralSubPermissions.push(...modifiedSubPermissionsArray.map<GeneralSubPermission>((item: GeneralSubPermission) => item));
		});

		Object.values(modifiedHardwarePermissions)?.forEach((digitrackPermissionItem: DigiTracPermissionItem) => {
			const currentDigitrackPermissionItems: HardwarePermission[] = Object.values(digitrackPermissionItem.Items) as HardwarePermission[];
			if (currentDigitrackPermissionItems?.length > 0) {
				role.HardwarePermissions.push(...currentDigitrackPermissionItems);
			}
		});

		deviceAdminApi.editRole(role).then(res => {
			NotificationStatus({
				responseData: res,
				notUseDefaultNotification: true,
				onSuccessCallback: () => {
					if (redirectTo) {
						window.location.href = redirectTo;
					} else {
						batch(() => {
							setIsShowUnsavedWarningModal(false);
							completeBackNavigationToRolesList(true);
							dispatchLocal(setClearIsDirtyAction());
						});
					}
				},
				onFailedValidation: () => {
					batch(() => {
						if (res.AdditionalResponseInfo === 'admin') {
							handleErrorNotification(res.ErrorMessage);
						} else {
							setIsRoleNameDuplicate(true);
							handleErrorNotification();
						}
						setIsLoading(false);
					});
				},
				onPermissionErrorCallback: async () => {
					notification['error']({
						message: res.ErrorMessage,
					});
				},
			});
		});
	};

	return (
		<>
			<Spin wrapperClassName={styles.spin} size='large' tip={`${_('Loading')}...`} spinning={isLoading}>
				<div className={styles.container}>
					<div>
						<Breadcrumb className={styles.breadCrumbContainer}>
							<Breadcrumb.Item>
								<a onClick={() => handleBackRolesList()}>
									<ArrowLeftOutlined /> <label id='go-back-label-roles'>{`${_('DeviceControl')} / ${_('Roles')} / ${originalName}`}</label>
								</a>
							</Breadcrumb.Item>
						</Breadcrumb>
						<div className={styles.roleHeader}>
							<label className={styles.labelHeader}>{`${_('ConfigureRoleTitle')} ${originalName}`}</label>
							<HelpFactoryComponent helpFeature={HelpFeature.Roles} topicId={topicId} />
						</div>
						<div className={styles.subContainer}>
							<label id='roleNameLabel' htmlFor='roleNameInput' className={styles.labelInput}>
								{_('Name')}:
							</label>
							<div>
								<Input
									id='roleNameInput'
									className={cx(styles.input, {
										[styles.error]: isRoleNameDuplicate || isRoleNameEmpty,
									})}
									disabled={currentDevice.Id === 1}
									value={roleName}
									maxLength={32}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										dispatchLocal(setRolePropsContextModelAction({ isDirty: true, roleName: e.target.value }));
									}}
								/>
								{isRoleNameDuplicate && <p className={styles.errorMessage}>{roleNameDuplicateMessage}</p>}
								{isRoleNameEmpty && <p className={styles.errorMessage}>{emptyNameErrorMessage}</p>}
							</div>
							<label id='roleDescriptionLabel' htmlFor='roleDescriptionInput' className={styles.labelInput}>
								{_('Description')}:
							</label>
							<Input.TextArea
								className={styles.textAreaCustom}
								disabled={currentDevice.Id === 1}
								id='roleDescriptionInput'
								maxLength={125}
								onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
									dispatchLocal(setRolePropsContextModelAction({ isDirty: true, roleDescription: e.target.value }));
								}}
								rows={2}
								value={roleDescription}
							/>
						</div>
						<Tabs defaultActiveKey='1' items={items} className={styles.tabs} type='card' />
						<div className={styles.buttonsContainer}>
							<Button
								className={styles.buttonsStyle}
								id='roleSaveChangesButton'
								key='save'
								type='primary'
								onClick={onHandleSaveChanges}
								disabled={!isDirty}
								loading={isLoading}>
								{_('SaveChanges')}
							</Button>
							<Button id='roleCancelButton' key='cancel' onClick={handleBackRolesList}>
								{_('Cancel')}
							</Button>
						</div>
					</div>
				</div>
			</Spin>
			{onUnsavedChangesWarning()}
		</>
	);
};

export { ModalComponent };
